import React from 'react';
import cx from 'classnames';

import DetailDescriptionSection from 'components/BookingForm/DetailDescriptionSection/DetaiDescriptionSection';
import { PATIENT_LIST_TYPE } from 'components/BookingForm/Step3/Review/Review';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import { IPatientIdentity } from 'interfaces/familyBookingTypes';
import {
  MYSELF_AND_OTHERS_OPTION,
  ModeOption,
  OTHERS_OPTION,
} from 'pages/ClinicDetailsPage/BookNowPanel/utils/constants';
import { useAppSelector } from 'redux/reduxHooks';
import { BookableSelectedPatient } from 'services/APIs';
import { buildAvatarColorMap } from 'utils/avatarColor';
import { checkIsSamePatient } from 'utils/checkIsSamePatient';
import { getPatientKey } from 'utils/getPatientKey';

import PatientCard from '../PatientCard/PatientCard';
import UnbookablePatientCard from '../UnbookablePatientCard/UnbookablePatientCard';
import styles from './BookingPatients.module.scss';

interface BookingPatientsProps {
  description: React.ReactNode;
  bookingPatients: BookableSelectedPatient[];
  mode: ModeOption;
}

interface PatientGroup {
  type: string;
  matches: (patient: BookableSelectedPatient) => boolean;
  showsSelectedSlot: boolean;
}

const isUnbookable = (patient: BookableSelectedPatient) =>
  patient.isBookable === false;

const hasAppointmentWithinWindow = (patient: BookableSelectedPatient) =>
  patient.isExistingApptWithinDays === true;

const PATIENT_GROUPS: PatientGroup[] = [
  {
    type: PATIENT_LIST_TYPE.NORMAL,
    matches: (patient) => !!patient.isBookable,
    showsSelectedSlot: true,
  },
  {
    type: PATIENT_LIST_TYPE.INCOMPLETE_FUTURE_APPT,
    matches: (patient) =>
      isUnbookable(patient) &&
      patient.appointment?.isInPast === false &&
      !hasAppointmentWithinWindow(patient),
    showsSelectedSlot: false,
  },
  {
    type: PATIENT_LIST_TYPE.INCOMPLETE_PAST_APPT,
    matches: (patient) =>
      isUnbookable(patient) &&
      patient.appointment?.isInPast === true &&
      !hasAppointmentWithinWindow(patient),
    showsSelectedSlot: false,
  },
  {
    type: PATIENT_LIST_TYPE.EXISTING_FUTURE_APPT,
    matches: (patient) =>
      isUnbookable(patient) && hasAppointmentWithinWindow(patient),
    showsSelectedSlot: false,
  },
];

const BookingPatients = ({
  description,
  bookingPatients,
  mode,
}: BookingPatientsProps) => {
  const {
    familyBookingSlice: { selectedPatients, contactUser },
    clinicDetailsSlice: { clinicData },
  } = useAppSelector((state) => state);

  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const pointOfContactPatient =
    mode.id === MYSELF_AND_OTHERS_OPTION.id ? selectedPatients[0] : contactUser;

  const avatarColorByPatient = buildAvatarColorMap([
    getPatientKey(pointOfContactPatient),
    ...bookingPatients.map((patient) => getPatientKey(patient)),
  ]);

  const getColorIndex = (patient: IPatientIdentity) =>
    avatarColorByPatient.get(getPatientKey(patient)) ?? 0;

  const isPointOfContact = (patient: IPatientIdentity) =>
    mode.id === MYSELF_AND_OTHERS_OPTION.id &&
    checkIsSamePatient(pointOfContactPatient, patient);

  return (
    <div
      className={cx(styles['container'], {
        [styles['container-iframe']]: isLoadedInsideIframe,
      })}
    >
      {mode.id === OTHERS_OPTION.id && (
        <div className={styles['card-container']}>
          <PatientCard
            patient={pointOfContactPatient}
            isPointOfContact
            colorIndex={getColorIndex(pointOfContactPatient)}
          />

          {description && (
            <div className={styles['description']}>{description}</div>
          )}
        </div>
      )}

      {PATIENT_GROUPS.map(({ type, matches, showsSelectedSlot }) => {
        const groupPatients = bookingPatients.filter(matches);

        if (groupPatients.length === 0) return null;

        return (
          <div key={type} className={styles['patient-list-section']}>
            <div className={styles['description-section']}>
              <DetailDescriptionSection
                type={type}
                patients={bookingPatients}
                isFamilyBooking
                clinicInfo={{
                  name: clinicData.name,
                  phoneNumber: clinicData.phone,
                  email: clinicData.email,
                }}
                futureAppointmentWindowDays={
                  clinicData.futureAppointmentWindowDays
                }
              />
            </div>

            <div className={styles['card-container']}>
              {groupPatients.map((patient) =>
                showsSelectedSlot ? (
                  <PatientCard
                    key={getPatientKey(patient)}
                    patient={patient}
                    isBookable={type === PATIENT_LIST_TYPE.NORMAL}
                    isPointOfContact={isPointOfContact(patient)}
                    colorIndex={getColorIndex(patient)}
                  />
                ) : (
                  <UnbookablePatientCard
                    key={getPatientKey(patient)}
                    patient={patient}
                    isPointOfContact={isPointOfContact(patient)}
                    colorIndex={getColorIndex(patient)}
                  />
                )
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BookingPatients;
